import React from 'react';
import Joi from 'joi';

import { Link, Location, NavigateFunction } from 'react-router-dom';
import { getErrorMessage } from '../../utils/utilities';

import Conditional from '../common/conditional';
import CustomMicrosoftLogin from '../common/customMicrosoftLogin';
import Form from '../common/form';
import Progress from '../themed/progress';

import withRouter from '../hoc/withRouter';
import routes from '../../utils/routes';

import authService from '../../services/authService';
import cookieService from '../../services/cookieService';
import toastService from '../../services/toastService';

import config from '../../utils/config';
import css from '../pages/auth/auth.module.scss';

interface LoginFormProps {
	location: Location;
	navigate: NavigateFunction;
}

class LoginForm extends Form {
	state = {
		data: { email: '', password: '', remember: false, },
		errors: {},
		loading: false,
		progress: 0,
	};

	schema = {
		email: Joi.string().email({ tlds: false }).required().label('Email Address'),
		password: Joi.string().required().label('Password'),
		remember: Joi.boolean().default(false).required().label('Remember Me')
	};

	componentDidMount() {
		this.restorePersistedState();
	}

	restorePersistedState = async () => {
		const session = cookieService.retrieveUserSession();
		if (!session || !session.credentials) return;
	
		this.setState({
			data: {
				email: session.credentials.email,
				password: session.credentials.password,
				remember: true,
			}
		});
	};

	login = async () => {
		await authService.loginWithCredentials(this.state.data, this.handleProgressChange);

		const props = this.props as LoginFormProps;
		const route: string = props.location.state ? props.location.state.from : routes.internalUserDashboard.pathname;

		window.location.pathname = route;
	};

	doSubmit = (): void => {
		toastService.promise(this.login(), {
			loading: 'Authenticating',
			success: () => 'Authenticated Successfully',
			error: getErrorMessage,
		});
	};

	render() {
		return (
			<React.Fragment>
				<Progress progress={this.state.progress} />

				<Conditional isVisible={config.enableMicrosoftLogin}>
					<div className="d-flex justify-content-center">
						<CustomMicrosoftLogin />
					</div>

					<div className={css.newLoginLeftDivider}>
						<div className={css.newLoginLeftDividerStroke}></div>
						<div className={css.newLoginLeftDividerCaption}>or</div>
						<div className={css.newLoginLeftDividerStroke}></div>
					</div>
				</Conditional>

				<form autoComplete="off" onSubmit={this.handleSubmit}>
					{this.renderInput({
						type: 'email',
						name: 'email',
						placeholder: 'e.g. johndoe@russelsmithgroup.com',
						autoComplete: 'email',
						label: 'Email Address',
					})}

					{this.renderInput({
						type: 'password',
						name: 'password',
						placeholder: 'Enter your password',
						label: 'Password',
						autoComplete: 'password',
					})}
					
					<div className="my-4">
						{this.renderSwitch({
							name: 'remember',
							label: 'Remember Me',
						})}
					</div>

					<div className="my-3 d-flex justify-content-between align-items-center">
						{this.renderButton('Login')}

						<Link
							to={routes.forgotPassword.fullPath}
							className={css.newLoginForgotPassword}
						>
							Forgot Password?
						</Link>
					</div>
				</form>
			</React.Fragment>
		);
	}
}

export default withRouter(LoginForm);
